import { cn } from "@/lib/hooks/utils";
import { Check, ChevronsUpDown, Plus } from "lucide-react";
import * as React from "react";
import { useCallback, useEffect } from "react";
import { Button } from "./button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./command";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import Preloader from "./preloader";

interface Paginated<T> {
  docs: T[];
  hasNextPage: boolean;
}

interface Pagination {
  pageIndex: number;
  pageSize: number;
}

interface SearchableDropdownProps<T> {
  fetchData: (pagination: Pagination, searchText: string) => Promise<Paginated<T>>;
  renderOption: (item: T) => { label: string; value: string };
  onSelect: (value: string, title: string) => void;
  onCreateNew?: (inputText: string) => void;
  selectedItem?: {
    label: string;
    value: string;
  };
  placeholder?: string;
  createNewLabel?: string;
}

export function SearchableDropdown<T>({
  fetchData,
  renderOption,
  onSelect,
  onCreateNew,
  selectedItem,
  placeholder = "Search...",
  createNewLabel = "Create new",
}: SearchableDropdownProps<T>) {
  const [open, setOpen] = React.useState(false);
  const [value, setValue] = React.useState<{ label: string; value: string } | undefined>(selectedItem);
  const [inputText, setInputText] = React.useState("");
  const [data, setData] = React.useState<T[]>([]);
  const [pagination, setPagination] = React.useState<Pagination>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [hasMore, setHasMore] = React.useState(true);
  const [loading, setLoading] = React.useState(false);

  const fetchMoreData = useCallback(
    async (reset?: boolean, includeInput?: boolean) => {
      if ((!hasMore || loading) && !reset) return;

      setLoading(true);
      try {
        const newPagination = reset ? { ...pagination, pageIndex: 0 } : pagination;
        const result = await fetchData(newPagination, includeInput || includeInput === undefined ? inputText : "");

        if (reset) {
          setData(result.docs);
          setHasMore(result.hasNextPage);
        } else {
          setData((prev) => [...prev, ...result.docs]);
          setHasMore(result.hasNextPage);
          setPagination((prev) => ({
            ...prev,
            pageIndex: prev.pageIndex + 1,
          }));
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [inputText, pagination]
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      if (inputText.length > 4) {
        fetchMoreData(true, true);
      } else if (inputText.length === 0) {
        fetchMoreData(true);
      }
    }, 300);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputText]);

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      const scrollThreshold = scrollHeight - scrollTop - clientHeight;

      if (scrollThreshold < 50 && !loading && hasMore) {
        fetchMoreData();
      }
    },
    [fetchMoreData, loading, hasMore]
  );

  const handleCreateNew = () => {
    onCreateNew && onCreateNew(inputText);
    setOpen(false);
    setInputText("");
  };

  return (
    <Popover
      open={open}
      onOpenChange={(openChange) => {
        setOpen(openChange);
        if (!openChange) {
          setInputText("");
          fetchMoreData(true);
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" aria-expanded={open} className="w-full justify-between">
          {value?.label ?? placeholder}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Eingabe zum Suchen..." value={inputText} onValueChange={setInputText} />
          <CommandList onScroll={handleScroll}>
            {data.length > 0 ? (
              <CommandGroup>
                {data.map((item, index) => {
                  const { label, value: itemValue } = renderOption(item);
                  return (
                    <CommandItem
                      key={index}
                      value={itemValue}
                      onSelect={(currentValue) => {
                        setValue({ label, value: currentValue });
                        setOpen(false);
                        onSelect(currentValue, label);
                      }}
                    >
                      <Check className={cn("mr-2 h-4 w-4", value?.value === itemValue ? "opacity-100" : "opacity-0")} />
                      {label}
                    </CommandItem>
                  );
                })}
                {loading && <Preloader />}
              </CommandGroup>
            ) : (
              <>
                {inputText.length > 0 && createNewLabel ? (
                  <CommandGroup>
                    <CommandItem onSelect={handleCreateNew} className="flex items-center gap-2 text-blue-600">
                      <Plus className="h-4 w-4" />
                      {createNewLabel}: {inputText}
                    </CommandItem>
                  </CommandGroup>
                ) : (
                  <CommandEmpty>No results found.</CommandEmpty>
                )}
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
